import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { Loader2 } from "lucide-react";
import api, { formatApiError } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { LOGO_URL } from "@/data/quiz";

export default function AdminLogin() {
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);
  const { admin, login } = useAuth();
  const navigate = useNavigate();

  useEffect(() => {
    if (admin) navigate("/admin", { replace: true });
  }, [admin, navigate]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError("");
    setLoading(true);
    try {
      const { data } = await api.post("/auth/login", { email, password });
      login(data);
      navigate("/admin", { replace: true });
    } catch (err) {
      setError(formatApiError(err.response?.data?.detail) || err.message);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen flex items-center justify-center bg-[#faf8f5] px-4" data-testid="admin-login-page">
      <div className="w-full max-w-md">
        <div className="text-center mb-8">
          <img src={LOGO_URL} alt="The Weave" className="h-9 w-auto mx-auto mb-5" />
          <h1 className="text-2xl font-semibold text-[#1e292b]">Admin sign in</h1>
          <p className="mt-1 text-sm text-[#5a686b]">Founder Academy quiz submissions</p>
        </div>
        <form onSubmit={handleSubmit} className="bg-white border border-[#e2ded7] rounded-2xl p-6 sm:p-8 shadow-sm space-y-5">
          <div>
            <label htmlFor="admin-email" className="block text-sm font-medium text-[#1e292b] mb-1.5">Email</label>
            <input
              id="admin-email"
              type="email"
              required
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              data-testid="admin-email-input"
              className="w-full rounded-xl border-2 border-[#e2ded7] bg-white px-4 py-3 text-base focus:border-[#4aa0a4] focus:outline-none focus-visible:ring-2 focus-visible:ring-[#4aa0a4]/25 transition-colors"
            />
          </div>
          <div>
            <label htmlFor="admin-password" className="block text-sm font-medium text-[#1e292b] mb-1.5">Password</label>
            <input
              id="admin-password"
              type="password"
              required
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              data-testid="admin-password-input"
              className="w-full rounded-xl border-2 border-[#e2ded7] bg-white px-4 py-3 text-base focus:border-[#4aa0a4] focus:outline-none focus-visible:ring-2 focus-visible:ring-[#4aa0a4]/25 transition-colors"
            />
          </div>
          {error && (
            <p className="text-sm text-[#e93e36]" data-testid="admin-login-error">{error}</p>
          )}
          <button
            type="submit"
            disabled={loading}
            data-testid="admin-login-button"
            className="w-full inline-flex items-center justify-center gap-2 bg-[#4aa0a4] text-white font-medium px-6 py-3 rounded-full hover:bg-[#3b868a] transition-all shadow-md active:scale-[0.98] disabled:opacity-50"
          >
            {loading && <Loader2 className="h-4 w-4 animate-spin" />}
            Sign in
          </button>
        </form>
        <p className="text-center mt-6">
          <a href="/" className="text-sm text-[#5a686b] hover:text-[#1e292b] underline underline-offset-4">
            Back to the quiz
          </a>
        </p>
      </div>
    </div>
  );
}
